import { useCallback, useMemo, useState } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import Raven from 'raven-js';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'cust... Remove this comment to see the full error message

import { ACTIVITY_CREATOR_INTERACTIONS } from 'customer-data-tracking/constants/eventNames';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { ObjectTypeFromIds } from 'customer-data-objects/constants/ObjectTypeIds';
import LabelledIconButton from 'crm-activity-creator-widgets/buttons/LabelledIconButton';
import { useButtonUpgradePath } from 'crm-activity-creator-widgets/hooks/useButtonUpgradePath';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import OverflowActivityOptionLabel from 'crm-activity-creator-widgets/buttons/OverflowActivityOptionLabel';
import { getTranslatedButtonAction, getTranslatedButtonAriaLabel, getTranslatedButtonLabel } from 'crm-activity-creator-widgets/helpers/buttonTranslations';
import { getButtonIcon, getButtonIconName } from 'crm-activity-creator-widgets/helpers/buttonIcons';
import getSource from 'crm-activity-creator-widgets/helpers/getSource';
import { Metrics } from 'crm-activity-creator-data/utils/Metrics';
import { WINDOW_ERRORS, MESSAGE_BUS } from 'crm-activity-creator-data/constants/errors';
import { ShinySkeletonText } from 'crm-activity-creator-widgets/buttons/skeleton/ShinySkeletonText';
import { ShinySkeletonCircle } from './skeleton/ShinySkeletonCircle';
import ActivityButtonSkeleton from './skeleton/ActivityButtonSkeleton';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { useOverrideCreateButtonContext } from 'crm-activity-creator-data/context/OverrideCreateButtonContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// default window size for all interactions but meeting
const WINDOWED = 'windowed';
const SKELETON_TEXT_WIDTH = 150;
function CreateActivityButton({
  buttonProps,
  buttonSize,
  dataTestId,
  disabled = false,
  interactionName,
  isError = false,
  isLoading = false,
  isOverflow = false,
  onboardingSelector,
  seleniumSelector,
  tooltipMessage,
  objectTypeId,
  onClick = emptyFunction,
  objectId,
  showCarrot = false,
  taskId,
  windowVariant = WINDOWED
}) {
  const [hasError, setHasError] = useState(isError);
  const sendCrmMessage = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ACTIVITY_CREATOR);
  const OverrideButtonComponent = useOverrideCreateButtonContext();
  const buttonUpgradePoint = useButtonUpgradePath(interactionName);
  const {
    openUpgradeModal
  } = useUpgradeModal(buttonUpgradePoint && buttonUpgradePoint.upgradeKey || '');
  const sendOpenMessage = useCallback(() => {
    sendCrmMessage({
      interactionType: interactionName,
      objectTypeId,
      objectId: Number(objectId),
      taskId,
      windowVariant
    });
    onClick();
  }, [sendCrmMessage, interactionName, objectTypeId, objectId, taskId, windowVariant, onClick]);
  const logACButtonClicked = useCallback(() => {
    CrmLogger.log(ACTIVITY_CREATOR_INTERACTIONS, {
      action: 'activity button clicked',
      activity: interactionName,
      objectType: ObjectTypeFromIds[objectTypeId] || objectTypeId,
      source: getSource(isOverflow)
    });
  }, [interactionName, isOverflow, objectTypeId]);
  const openInteractionWindow = useCallback(() => {
    try {
      sendOpenMessage();
    } catch (error) {
      setHasError(true);
      Metrics.counter(WINDOW_ERRORS, {
        error_type: MESSAGE_BUS,
        interaction_type: interactionName
      }).increment();
      Raven.captureMessage('Error in activity creator button on opening window', {
        fingerprint: ['ERROR_OPEN_INTERACTION_WINDOW_FAILED'],
        extra: {
          objectTypeId,
          subjectId: objectId,
          interactionName
        }
      });
    }
    return onClick();
  }, [interactionName, objectId, objectTypeId, onClick, sendOpenMessage]);
  const handleClick = useCallback(() => {
    logACButtonClicked();
    if (buttonUpgradePoint && !buttonUpgradePoint.hasScope) {
      return openUpgradeModal();
    }
    if (disabled) {
      return emptyFunction;
    }
    return buttonProps ? buttonProps.onClick() : openInteractionWindow();
  }, [buttonUpgradePoint, disabled, logACButtonClicked, buttonProps, openInteractionWindow, openUpgradeModal]);
  const renderSkeletonState = useMemo(() => {
    return isOverflow ? /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      paddingBlock: "200",
      children: [/*#__PURE__*/_jsx(View, {
        marginInline: "500",
        children: /*#__PURE__*/_jsx(ShinySkeletonCircle, {
          size: 16
        })
      }), /*#__PURE__*/_jsx(ShinySkeletonText, {
        width: SKELETON_TEXT_WIDTH
      })]
    }) : /*#__PURE__*/_jsx(ActivityButtonSkeleton, {
      size: buttonSize
    });
  }, [buttonSize, isOverflow]);
  const ariaLabel = getTranslatedButtonAriaLabel(interactionName);
  const label = getTranslatedButtonLabel(interactionName);

  // if there is a tooltip passed in and the button is not in an error state show the tooltip
  // otherwise show the interaction action as the tooltip
  const tooltip = !isError && tooltipMessage ? tooltipMessage : getTranslatedButtonAction(interactionName);
  if (isLoading) {
    return renderSkeletonState;
  }
  if (OverrideButtonComponent) {
    return /*#__PURE__*/_jsx(OverrideButtonComponent, {
      ariaLabel: ariaLabel,
      seleniumSelector: seleniumSelector,
      dataTestId: dataTestId,
      disabled: disabled,
      IconComponent: getButtonIcon(interactionName),
      iconName: getButtonIconName(interactionName),
      isError: hasError,
      interactionName: interactionName,
      onClick: handleClick,
      label: label,
      tooltip: tooltip
    }, interactionName);
  }
  if (isOverflow) {
    return /*#__PURE__*/_jsx(OverflowActivityOptionLabel, {
      handleClick: handleClick,
      IconComponent: getButtonIcon(interactionName),
      interactionName: interactionName,
      isDisabled: disabled,
      isError: hasError,
      label: getTranslatedButtonAction(interactionName),
      showCarrot: showCarrot,
      tooltip: tooltip
    });
  }
  return /*#__PURE__*/_jsx(LabelledIconButton, {
    ariaLabel: ariaLabel,
    buttonSize: buttonSize,
    onboardingSelector: onboardingSelector,
    seleniumSelector: seleniumSelector,
    dataTestId: dataTestId,
    disabled: disabled,
    hasUpgradePath: buttonUpgradePoint && !buttonUpgradePoint.hasScope,
    IconComponent: getButtonIcon(interactionName),
    iconName: getButtonIconName(interactionName),
    isError: hasError,
    onClick: handleClick,
    label: label,
    tooltip: tooltip
  }, interactionName);
}
export default CreateActivityButton;